import { FC, useEffect, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import { trpc } from 'src/trpc';
import { useToast } from 'src/components/useToast';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Loader } from 'src/components/Loader';
import { formatPhone } from 'src/utils/format-phone';
import { useDebounce } from 'src/components/Debounce';
import { Check, Search, UserPlus } from 'lucide-react';
import {
  CreateCustomerSubview,
  type CreatedCustomer,
} from './CreateCustomerSubview';

interface CustomerCorrectionTabProps {
  orderId: string;
  currentCustomer?: {
    _id?: string;
    userId?: string;
    name?: { first: string; last: string } | null;
    email?: string;
    phone?: string;
  } | null;
  onCompleted: () => void;
  /** Reports create/assign activity up so the drawer can refuse to dismiss mid-flight. */
  onBusyChange?: (isBusy: boolean) => void;
}

const SEARCH_INPUT_ID = 'order-customer-search';

export const CustomerCorrectionTab: FC<CustomerCorrectionTabProps> = ({
  orderId,
  currentCustomer,
  onCompleted,
  onBusyChange,
}) => {
  const params = useParams();
  const utils = trpc.useUtils();
  const { successToast, errorToast } = useToast();

  const [view, setView] = useState<'search' | 'create'>('search');
  const [search, setSearch] = useState('');
  const debouncedSearch = useDebounce(search, 500);
  const [selectedUserId, setSelectedUserId] = useState<string | null>(null);
  // Set once a customer has been created but the order is not yet on them. Keeping it lets a
  // failed assignment be retried against that customer instead of making the admin create or
  // find them a second time.
  const [pendingTarget, setPendingTarget] = useState<CreatedCustomer | null>(null);
  const [assignmentError, setAssignmentError] = useState<string | null>(null);

  const errorRef = useRef<HTMLParagraphElement>(null);

  const { data: results, isFetching } = trpc.admin.customer.adminListCustomers.useQuery(
    { search: debouncedSearch, limit: 10, offset: 0 },
    { enabled: debouncedSearch.trim().length > 1 },
  );

  const candidates = (results?.customers || []).filter(
    (customer) => customer.userId !== currentCustomer?.userId,
  );

  const selected = candidates.find((customer) => customer.userId === selectedUserId);

  const { mutate: reassign, isPending: isAssigning } =
    trpc.admin.order.adminReassignOrderCustomer.useMutation({
      onSuccess: () => {
        utils.admin.order.adminGetOrderRelatedInfoById.invalidate(params.id!);
        utils.admin.note.getNotes.invalidate();
        successToast({ title: 'Order moved to the selected customer' });
        setSelectedUserId(null);
        setSearch('');
        setPendingTarget(null);
        setAssignmentError(null);
        onCompleted();
      },
      onError: (error) => {
        setAssignmentError(error.message || 'Could not move this order to that customer.');
        errorToast({ title: 'Could not move this order', description: error.message });
      },
    });

  const { mutate: createCustomer, isPending: isCreating } =
    trpc.admin.customer.adminCreateCustomer.useMutation({
      onSuccess: (result, variables) => {
        utils.admin.customer.adminListCustomers.invalidate();
        const created: CreatedCustomer = {
          userId: result.userId,
          name: variables.name,
          email: variables.email,
          phone: variables.phone,
        };
        // Creating a customer under an order is a correction, so the order moves onto them
        // straight away rather than leaving the admin to re-find who they just created.
        setPendingTarget(created);
        setAssignmentError(null);
        reassign({ orderId, targetUserId: created.userId });
      },
      onError: (error) => {
        setAssignmentError(error.message || 'Could not create this customer.');
        errorToast({ title: 'Could not create this customer', description: error.message });
      },
    });

  const isBusy = isCreating || isAssigning;

  useEffect(() => {
    onBusyChange?.(isBusy);
  }, [isBusy, onBusyChange]);

  // An error the admin cannot see is an error they cannot act on, so focus follows it.
  useEffect(() => {
    if (assignmentError) errorRef.current?.focus();
  }, [assignmentError]);

  const displayName = (customer?: { name?: { first: string; last: string } | null } | null) =>
    `${customer?.name?.first || ''} ${customer?.name?.last || ''}`.trim() || 'Unnamed customer';

  const currentCustomerName = displayName(currentCustomer);

  const searchStatus = () => {
    if (isFetching) return 'Searching customers';
    if (debouncedSearch.trim().length > 1 && !candidates.length) {
      return 'No other customers matched that search.';
    }
    if (candidates.length) {
      return `${candidates.length} customer${candidates.length === 1 ? '' : 's'} found`;
    }
    return '';
  };

  const errorBlock = assignmentError && (
    <p
      ref={errorRef}
      tabIndex={-1}
      role="alert"
      className="rounded-lg bg-red-50 p-3 text-sm text-red-700 outline-none focus-visible:ring-2 focus-visible:ring-ring"
    >
      {assignmentError}
    </p>
  );

  // Creation succeeded but the order never moved. The customer exists, so the way forward is
  // another attempt at the assignment - not another trip through search or the create form.
  if (pendingTarget && assignmentError && !isBusy) {
    return (
      <div className="space-y-4">
        <h4 className="text-sm font-semibold text-gray-900">Finish assigning this order</h4>
        <div className="card bg-slate-50 p-4 flex flex-col items-stretch justify-start gap-1">
          <p className="text-sm font-medium text-text-primary">{displayName(pendingTarget)}</p>
          {pendingTarget.email && <p className="text-xs text-muted">{pendingTarget.email}</p>}
          <p className="text-xs text-muted">
            This customer was created. The order has not moved to them yet.
          </p>
        </div>
        {errorBlock}
        <Button
          className="w-full"
          disabled={isBusy}
          onClick={() => {
            setAssignmentError(null);
            reassign({ orderId, targetUserId: pendingTarget.userId });
          }}
        >
          Retry assignment
        </Button>
      </div>
    );
  }

  if (view === 'create') {
    return (
      <div className="space-y-4">
        {errorBlock}
        <CreateCustomerSubview
          isBusy={isBusy}
          onBack={() => {
            setAssignmentError(null);
            setView('search');
          }}
          onCreate={(input) => createCustomer(input)}
        />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Current customer */}
      <div>
        <h4 className="text-sm font-semibold text-gray-900 mb-3">Current customer</h4>
        <div className="card bg-slate-50 p-4 flex flex-col items-stretch justify-start">
          <p className="text-sm font-medium text-text-primary">{currentCustomerName}</p>
          {currentCustomer?.email && (
            <p className="text-xs text-muted mt-1">{currentCustomer.email}</p>
          )}
          {currentCustomer?.phone && (
            <p className="text-xs text-muted mt-1">
              {formatPhone(currentCustomer.phone.replace('+1', ''))}
            </p>
          )}
        </div>
      </div>

      <div className="border-t border-gray-200" />

      {/* Reassign to an existing customer */}
      <div>
        <h4 className="text-sm font-semibold text-gray-900 mb-3">Choose an existing customer</h4>

        <Label htmlFor={SEARCH_INPUT_ID} className="label text-xs">
          Search customers
        </Label>
        <div className="relative mt-1">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted" />
          <Input
            id={SEARCH_INPUT_ID}
            className="pl-9"
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            placeholder="Name, email, or phone number"
          />
        </div>

        {/* Search is asynchronous and its outcome is purely visual otherwise, so the result
            count, the empty state and the loading state are all announced. */}
        <p className="sr-only" role="status" aria-live="polite">
          {searchStatus()}
        </p>

        <div className="mt-3 space-y-2">
          {isFetching && <Loader />}

          {!isFetching && debouncedSearch.trim().length > 1 && !candidates.length && (
            <p className="text-xs text-muted">No other customers matched that search.</p>
          )}

          {!isFetching && !!candidates.length && (
            <div role="radiogroup" aria-label="Matching customers" className="space-y-2">
              {candidates.map((customer) => {
                const isSelected = customer.userId === selectedUserId;
                return (
                  <button
                    key={customer._id?.toString()}
                    type="button"
                    role="radio"
                    aria-checked={isSelected}
                    onClick={() => setSelectedUserId(customer.userId!)}
                    className={`flex w-full items-center justify-between rounded-lg border p-3 text-left transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 ${
                      isSelected
                        ? 'border-primary bg-primary/5'
                        : 'border-gray-200 hover:border-gray-300'
                    }`}
                  >
                    <span>
                      <span className="block text-sm font-medium text-text-primary">
                        {displayName(customer)}
                      </span>
                      <span className="block text-xs text-muted">
                        {[customer.email, customer.phone].filter(Boolean).join(' · ')}
                      </span>
                    </span>
                    {isSelected && <Check className="h-4 w-4 text-primary" />}
                  </button>
                );
              })}
            </div>
          )}
        </div>

        <div className="mt-4">
          <Button
            variant="outline"
            size="sm"
            className="h-11"
            disabled={isBusy}
            onClick={() => setView('create')}
          >
            <UserPlus className="h-4 w-4 mr-2" />
            Create new customer
          </Button>
        </div>
      </div>

      {errorBlock}

      {selected && (
        <div className="card bg-slate-50 p-4 flex flex-col items-stretch justify-start gap-3">
          <h4 className="text-sm font-semibold text-gray-900">Confirm the change</h4>
          <div className="grid grid-cols-2 gap-4">
            <div className="min-w-0">
              <p className="label text-xs">From</p>
              <p className="text-sm font-medium text-text-primary break-words">
                {currentCustomerName}
              </p>
            </div>
            <div className="min-w-0">
              <p className="label text-xs">To</p>
              <p className="text-sm font-medium text-text-primary break-words">
                {displayName(selected)}
              </p>
            </div>
          </div>
          <Button
            className="w-full"
            disabled={isBusy}
            onClick={() => {
              setAssignmentError(null);
              reassign({ orderId, targetUserId: selected.userId! });
            }}
          >
            {isAssigning ? 'Assigning order…' : 'Assign order to this customer'}
          </Button>
        </div>
      )}
    </div>
  );
};
